import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWalletBalance } from '../store/walletSlice';
import { tonWithdrawalAPI } from '../../../services/api';
import { useLinkedWallet } from '../hooks/useLinkedWallet';
import Preloader from '../../../shared/components/Preloader';
import '../styles/tonWithdrawalPage.css';
import { useTranslation } from 'react-i18next';
import { msg, tr } from '../../../i18n/msg';
import { locale } from '../../../i18n/format';

const MIN_WITHDRAWAL = 10;

const STATUS_LABELS = {
  PENDING: msg('withdrawal.status.pending', 'PENDING'),
  APPROVED: msg('withdrawal.status.approved', 'APPROVED'),
  REJECTED: msg('withdrawal.status.rejected', 'REJECTED'),
  CANCELLED: msg('withdrawal.status.cancelled', 'CANCELLED'),
};

// Типы transaction_logs по GRAM-балансу (TonWithdrawalController → type)
const TX_TYPE_LABELS = {
  TON_CREDIT: msg('ton_tx.ton_credit', 'GRAM credit'),
  TON_WITHDRAWAL_HOLD: msg('ton_tx.ton_withdrawal_hold', 'Withdrawal'),
  TON_PURCHASE: msg('ton_tx.ton_purchase', 'Purchase'),
  PVP_STAKE: msg('ton_tx.pvp_stake', 'PvP stake'),
  PVP_WIN: msg('ton_tx.pvp_win', 'PvP win'),
  PVP_REFUND: msg('ton_tx.pvp_refund', 'PvP refund'),
  LUCKY_SPIN: msg('ton_tx.lucky_spin', 'Lucky Spin'),
  LOOTBOX_OPEN: msg('ton_tx.lootbox_open', 'Chest opened'),
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
}

function shortAddress(addr) {
  if (!addr) return '';
  return addr.slice(0, 8) + '...' + addr.slice(-6);
}

function formatTon(val) {
  if (val === null || val === undefined) return '0';
  const n = Number(val);
  return n % 1 === 0 ? n.toString() : n.toFixed(4).replace(/\.?0+$/, '');
}

export default function TonWithdrawalPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tonBalance = useSelector((state) => state.wallet.ton) || 0;

  const { linkedWallet, isWalletLinked, connect, unlink } = useLinkedWallet();

  const [tonConnecting, setTonConnecting] = useState(false);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(null); // requestId being cancelled
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [tonTransactions, setTonTransactions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const canWithdraw = tonBalance >= MIN_WITHDRAWAL;

  const loadHistory = useCallback(async () => {
    try {
      const [requests, txs] = await Promise.all([
        tonWithdrawalAPI.getRequests(),
        tonWithdrawalAPI.getTonTransactions(),
      ]);
      setWithdrawalHistory(requests);
      setTonTransactions(txs);
    } catch (err) {
      console.error('Failed to load TON history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleConnectTon = async () => {
    if (tonConnecting || isWalletLinked) return;
    try {
      setTonConnecting(true);
      await connect();
    } catch (err) {
      console.error('TON wallet connection failed:', err);
    } finally {
      setTonConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await unlink();
    } catch (err) {
      console.error('TON wallet disconnect failed:', err);
    }
  };

  const handleCancel = async (requestId) => {
    if (cancelling) return;
    try {
      setCancelling(requestId);
      await tonWithdrawalAPI.cancelRequest(requestId);
      await loadHistory();
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error;
      setFormError(message || t('withdrawal.cancel_failed', 'Failed to cancel request'));
    } finally {
      setCancelling(null);
    }
  };

  const handleSubmit = async () => {
    setFormError('');
    setFormSuccess('');

    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      setFormError(t('withdrawal.invalid_amount', 'Enter a valid amount'));
      return;
    }
    if (parsed < MIN_WITHDRAWAL) {
      setFormError(t('withdrawal.minimum', 'Minimum withdrawal is {{min}} GRAM', { min: MIN_WITHDRAWAL }));
      return;
    }
    if (parsed > tonBalance) {
      setFormError(t('withdrawal.insufficient', 'Insufficient GRAM balance'));
      return;
    }
    if (!isWalletLinked) {
      setFormError(t('withdrawal.connect_first', 'Please connect your GRAM wallet first'));
      return;
    }

    try {
      setSubmitting(true);
      await tonWithdrawalAPI.createRequest(parsed);
      setFormSuccess(t('withdrawal.submitted', 'Withdrawal request submitted! We will process it shortly.'));
      setAmount('');
      await Promise.all([loadHistory(), dispatch(fetchWalletBalance())]);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error;
      setFormError(message || t('withdrawal.submit_failed', 'Failed to submit withdrawal request'));
    } finally {
      setSubmitting(false);
    }
  };

  const displayAddress = linkedWallet;

  const tonButtonLabel = (() => {
    if (tonConnecting) return t('wallet.connecting', 'Connecting...');
    return t('wallet.connect_gram', 'Connect GRAM Wallet');
  })();

  return (
    <div className="ton-withdrawal-page">
      <div className="ton-withdrawal-scroll">
        <div className="ton-withdrawal-inner">

          <h2 className="ton-withdrawal-title">{t('withdrawal.title', 'GRAM Withdrawal')}</h2>

          {/* Balance card */}
          <div className="ton-balance-card">
            <img src="/gram.png" alt="GRAM" />
            <div>
              <div className="ton-balance-label">{t('withdrawal.your_balance', 'Your GRAM balance')}</div>
              <div className="ton-balance-value">{formatTon(tonBalance)} GRAM</div>
              {!canWithdraw && (
                <div className="ton-balance-warning">
                  {t('withdrawal.min_required', 'Minimum {{min}} GRAM required for withdrawal', { min: MIN_WITHDRAWAL })}
                </div>
              )}
            </div>
          </div>

          {/* Wallet section */}
          <div className="ton-wallet-section">
            <div className="ton-wallet-section-title">{t('withdrawal.wallet', 'GRAM Wallet')}</div>

            {!isWalletLinked ? (
              <button
                className="app-btn app-btn--purple"
                onClick={handleConnectTon}
                disabled={tonConnecting}
              >
                {tonButtonLabel}
              </button>
            ) : (
              <>
                <div className="ton-wallet-address-row">
                  <div className="ton-wallet-address">{displayAddress}</div>
                </div>
                <button
                  className="app-btn app-btn--purple-outline"
                  onClick={handleDisconnect}
                >
                  {t('withdrawal.disconnect', 'Disconnect wallet')}
                </button>
              </>
            )}
          </div>

          {/* Withdrawal form */}
          <div className="ton-withdrawal-form">
            <div className="ton-form-label">{t('withdrawal.request', 'Request withdrawal')}</div>

            <div className="ton-amount-input-wrap">
              <input
                className="ton-amount-input"
                type="number"
                min={MIN_WITHDRAWAL}
                step="0.1"
                placeholder={t('withdrawal.placeholder', 'Min {{min}} GRAM', { min: MIN_WITHDRAWAL })}
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setFormError(''); setFormSuccess(''); }}
                disabled={!canWithdraw || !isWalletLinked || submitting}
              />
              <span className="ton-amount-currency">GRAM</span>
            </div>

            {formError && <div className="ton-form-error">{formError}</div>}
            {formSuccess && <div className="ton-form-success">{formSuccess}</div>}

            {!isWalletLinked && (
              <div className="ton-form-hint">{t('withdrawal.connect_hint', 'Connect your GRAM wallet to enable withdrawals')}</div>
            )}
            {isWalletLinked && !canWithdraw && (
              <div className="ton-form-hint">
                {t('withdrawal.need_at_least', 'You need at least {{min}} GRAM to request a withdrawal', { min: MIN_WITHDRAWAL })}
              </div>
            )}

            <button
              className="app-btn app-btn--purple"
              onClick={handleSubmit}
              disabled={!canWithdraw || !isWalletLinked || submitting}
            >
              {submitting ? t('withdrawal.submitting', 'Submitting...') : t('withdrawal.request_button', 'Request Withdrawal')}
            </button>
          </div>

          {/* Withdrawal requests history */}
          <div className="ton-history-section">
            <div className="ton-history-section-title">{t('withdrawal.requests', 'Withdrawal Requests')}</div>
            {historyLoading ? (
              <Preloader size="sm" />
            ) : withdrawalHistory.length === 0 ? (
              <div className="ton-history-empty">{t('withdrawal.requests_empty', 'No withdrawal requests yet')}</div>
            ) : (
              withdrawalHistory.map((item) => (
                <WithdrawalRequestItem key={item.id} item={item} onCancel={handleCancel} />
              ))
            )}
          </div>

          {/* TON transaction history */}
          <div className="ton-history-section">
            <div className="ton-history-section-title">{t('withdrawal.history', 'GRAM History')}</div>
            {historyLoading ? (
              <Preloader size="sm" />
            ) : tonTransactions.length === 0 ? (
              <div className="ton-history-empty">{t('withdrawal.history_empty', 'No GRAM transactions yet')}</div>
            ) : (
              tonTransactions.map((tx) => (
                <TonTransactionItem key={tx.id} tx={tx} />
              ))
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

function WithdrawalRequestItem({ item, onCancel }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const status = item.status || 'PENDING';
  const isPending = status === 'PENDING';

  const statusIcon = {
    PENDING: '⏳',
    APPROVED: '✅',
    REJECTED: '❌',
    CANCELLED: '↩',
  }[status] || '•';

  return (
    <div className={`ton-history-item ton-history-item--${status.toLowerCase()}`}>
      <div className={`ton-history-icon ton-history-icon--${status.toLowerCase()}`}>
        {statusIcon}
      </div>
      <div className="ton-history-info">
        <div className="ton-history-type">{t('withdrawal.request_item', 'Withdrawal Request')}</div>
        <div className="ton-history-wallet">{shortAddress(item.walletAddress)}</div>
        <span className={`ton-status-badge ton-status-badge--${status}`}>{tr(t, STATUS_LABELS[status]) || status}</span>
        <div className="ton-history-date">{formatDate(item.createdAt)}</div>
        {item.adminNote && <div className="ton-history-date">{item.adminNote}</div>}
        {isPending && (
          confirming ? (
            <div className="ton-cancel-confirm">
              <span>{t('withdrawal.cancel_confirm', 'Cancel and refund?')}</span>
              <button className="ton-cancel-yes" onClick={() => { setConfirming(false); onCancel(item.id); }}>{t('common.yes', 'Yes')}</button>
              <button className="ton-cancel-no" onClick={() => setConfirming(false)}>{t('common.no', 'No')}</button>
            </div>
          ) : (
            <button className="ton-cancel-btn" onClick={() => setConfirming(true)}>{t('common.cancel', 'Cancel')}</button>
          )
        )}
      </div>
      <div className={`ton-history-amount ton-history-amount--${status.toLowerCase()}`}>
        -{formatTon(item.amount)} GRAM
      </div>
    </div>
  );
}

function TonTransactionItem({ tx }) {
  const { t } = useTranslation();
  const isCredit = Number(tx.amount) >= 0;
  const kind = isCredit ? 'credit' : 'debit';
  const typeLabel = tr(t, TX_TYPE_LABELS[tx.type]) || (tx.type || 'TRANSACTION').replace(/_/g, ' ');

  return (
    <div className={`ton-history-item ton-history-item--${kind}`}>
      <div className={`ton-history-icon ton-history-icon--${kind}`}>
        {isCredit ? '💎' : '📤'}
      </div>
      <div className="ton-history-info">
        <div className="ton-history-type">{typeLabel}</div>
        <div className="ton-history-date">{formatDate(tx.createdAt)}</div>
      </div>
      <div className={`ton-history-amount ton-history-amount--${kind}`}>
        {isCredit ? '+' : ''}{formatTon(tx.amount)} GRAM
      </div>
    </div>
  );
}
